import { Record, List } from 'immutable';
import type { SelectFilterDefinitionTypeRecord } from '~/ducks/machine-filter-v2/data';
import type { FilterDefinitionType } from '~/ducks/machine-filter-v2/data/definitions';
import type {
  SharedFilterFields,
  SharedFilterMethods,
} from '../../types/filter-value-types';
import FilterDefinitionValueRecord from '../FilterDefinitionValueRecord';
import FilterDefinitionTypeConstants from '~/constants/filter-definition-type-constants';
import NameRecord from '~/ducks/machine-filter-v2/data/NameRecord';
import logger from '~/utils/logger/logger';

const keys: Fields = {
  filterName: new NameRecord(),
  selectedOptions: List(),
  type: FilterDefinitionTypeConstants.MULTI_SELECT,
};

export interface Fields extends SharedFilterFields {
  selectedOptions: List<FilterDefinitionValueRecord>;
  type: FilterDefinitionTypeConstants.MULTI_SELECT;
}

export default class SelectFilterRecord
  extends Record<Fields>(keys)
  implements SharedFilterMethods {
  constructor(values?) {
    super(values);

    const newValues: Partial<Fields> = {};
    if (values) {
      if (Array.isArray(values.selectedOptions)) {
        newValues.selectedOptions = List(
          values.selectedOptions.map(
            (val) => new FilterDefinitionValueRecord(val)
          )
        );
      }
    }
    return this.merge(newValues);
  }

  getFilterKey() {
    return this.filterName.getName();
  }

  getType() {
    return this.type;
  }

  getName() {
    return this.filterName.getName();
  }

  getLabel() {
    return this.filterName.getLabel();
  }

  getTranslationKey(): string {
    return this.filterName.getTranslationKey() || '';
  }

  getValues(): List<FilterDefinitionValueRecord> {
    return this.selectedOptions;
  }

  add(newValue: FilterDefinitionValueRecord) {
    if (this.selectedOptions.contains(newValue)) {
      return this;
    }
    return this.set('selectedOptions', this.selectedOptions.push(newValue));
  }

  removeValue(valueToRemove: FilterDefinitionValueRecord) {
    if (this.selectedOptions.contains(valueToRemove)) {
      if (this.selectedOptions.size <= 1) {
        return null;
      }
      return this.set(
        'selectedOptions',
        this.selectedOptions.filter((v) => v !== valueToRemove)
      );
    }

    logger.error('Filter value not found');
    return this;
  }

  updateFromDefinition(uncastDef: FilterDefinitionType): this {
    const def = uncastDef as SelectFilterDefinitionTypeRecord;
    const options = def.getOptions();

    const newValues = this.getValues().map((value) => {
      return options.find((o) => o.getValue() == value.getValue()) || value;
    });

    return this.merge({
      filterName: def.getNameRecord(),
      selectedOptions: newValues,
    });
  }
}
